<template>
  <v-dialog
    v-bind:content-class="contentClass"
    v-bind:max-width="maxWidth"
    v-bind:width="width"
    v-bind:persistent="persistent"
    v-bind:scrollable="scrollable"
    v-bind:value="value"
    v-on:input="onInput"
    v-on:click:outside="onClickOutside"
  >
    <template v-slot:activator="{ on }">
      <slot
        name="trigger"
        v-bind:listener-object="on" />
    </template>
    <v-card class="modal-dialog" v-bind:class="{ scrollable: scrollable }">
      <button
        v-if="withPreviousButton"
        v-on:click="onPreviousButton"
        type="button"
        class="modal-dialog__previous-button"
      >Назад</button>
      <button
        v-if="withCross"
        v-on:click="closeByCross"
        type="button"
        class="modal-dialog__cross"
      />
      <slot name="before-title" />
      <v-card-title class="text-h7 flex-column">
        <p
          class="modal-dialog__title"
          v-bind:class="[
            { 'modal-dialog__title--center': titleCenter },
            { 'modal-dialog__title--large': titleLarge },
          ]"
        >{{ title }}</p>
        <slot name="header" />
      </v-card-title>
      <v-card-text class="pb-0 justify-center">
        <slot />
      </v-card-text>
      <v-card-actions v-if="shownActions">
        <slot name="actions" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

/**
 * @description Название событие ввода.
 * @type {string}
 */
const NAME_EVENT_INPUT = 'input';

/**
 * @description Название события "Назад".
 * @type {string}
 */
const ON_CLICK_PREVIOUS_BUTTON = 'on-previous-button';

/**
 * @description Название событие клик снаружи модального окна.
 * @type {string}
 */
const ON_CLICK_OUTSIDE = 'on-click-outside';

/**
 * @description Модальный диалог.
 * @type {Vue}
 */
export default {
  computed: {
    /**
     * @description Показывать действия.
     * @type {boolean}
     */
    shownActions() {
      return this.$slots.actions;
    },
  },
  methods: {
    /**
     * Закрыть по нажатию на background
     * @returns {void}
     */
    onClickOutside() {
      this.$emit(ON_CLICK_OUTSIDE);
    },
    /**
     * Закрытие по нажатию крестика.
     * @returns {void}
     */
    closeByCross() {
      this.$emit(NAME_EVENT_INPUT, false);
    },
    /**
     * Обработать событие нажатия на кнопку "Назад".
     * @returns {void}
     */
    onPreviousButton() {
      this.$emit(ON_CLICK_PREVIOUS_BUTTON, false);
    },
    /**
     * @description Обработать ввод.
     * @param {boolean} isShown
     * @returns {void}
     */
    onInput(isShown) {
      this.$emit(NAME_EVENT_INPUT, isShown);
    },
  },
  name: 'ModalDialog',
  props: {
    /**
     * @description Пользовательский класс.
     * @type {string}
     */
    contentClass: { default: undefined, type: String },
    /**
     * @description Максимальная ширина.
     * @type {string}
     */
    maxWidth: { default: () => 'fit-content', type: String },
    /**
     * @description Фиксированная ширина.
     * @type {string}
     */
    width: { default: () => 'fit-content', type: String },
    /**
     * @description Предотвращать закрытие диалога при
     * клике за пределами элемента или при нажатии esc.
     * @type {boolean}
     */
    persistent: { default: () => false, type: Boolean },
    /**
     * @description Прокручиваемый.
     * @type {boolean}
     */
    scrollable: { default: () => false, type: Boolean },
    /**
     * @description Заголовок.
     * @type {string}
     */
    title: { required: true, type: String },
    /**
     * @description Выравнивание заголовка по центру.
     * @type {Boolean}
     */
    titleCenter: { default: () => false, type: Boolean },
    /**
     * @description Увеличенный адаптивный заголовок.
     * @type {Boolean}
     */
    titleLarge: { default: () => false, type: Boolean },
    /**
     * @description Показывать диалог.
     * @type {Boolean}
     */
    value: { required: true, type: Boolean },
    /**
     * @description Отобразить модальное окно с крестиком.
     * @type {boolean}
     */
    withCross: { default: () => false, type: Boolean },
    /**
     * @description Отобразить кнопку "Назад".
     * @type {boolean}
     */
    withPreviousButton: { default: () => false, type: Boolean },
  },
};
</script>

<style lang="scss" scoped>

::v-deep .v-dialog:not(.v-dialog--fullscreen) {
  &.position-start {
    align-self: flex-start;
  }
}

.modal-dialog {
  padding: 20px;
  &__title {
    margin-bottom: 10px !important;
    &--center {
      text-align: center;
    }
    &--large {
      font-size: 26px;
      @media screen and (max-width: 768px) {
        font-size: 24px;
      }
      @media screen and (max-width: 480px) {
        font-size: 20px;
      }
    }
  }
  &.scrollable {
    overflow: hidden;
  }
  .v-card__title {
    word-break: break-word;
  }
  .v-card__text {
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 15px;
  }
  &__previous-button {
    color: var(--action-color);
    display: flex;
    align-items: center;
    gap: 7px;
    margin: 0 0 10px 0;
    &:before {
      content: '';
      background-image: url('@/assets/img/arrow/arrow-blue.svg');
      background-position: center;
      background-size: contain;
      width: 12px;
      height: 12px;
      display: block;
    }
    &:hover {
      text-decoration: underline;
    }
  }
  &__cross {
    position: absolute;
    right: 15px;
    top: 15px;
    cursor: pointer;
    background-image: url("@/assets/img/exit-black.svg");
    background-repeat: no-repeat;
    background-size: contain;
    width: 17px;
    height: 17px;
  }
}

@media screen and (max-width: 620px) {
  .modal-dialog {
    padding: 20px 10px;
  }
}
</style>
